import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Skeleton,
  Alert,
  Button,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  ErrorOutline,
} from '@mui/icons-material';
import { mlAnalysisAPI, competitorsAPI, schedulingAPI } from '../services/api';
import MlEstimateBadge, { MlMetricLabel } from './MlEstimateBadge';
import useDebouncedValue from '../hooks/useDebouncedValue';

const DEBOUNCE_MS = 400;
const BEST_TIME_DEBOUNCE_MS = 400;

/**
 * EngagementPredictionPanel - Task 2.6 / Phase 16.R7
 * Predict API uses debounced caption; best-times do NOT re-fetch on every keystroke.
 */
function EngagementPredictionPanel({
  content = '',
  platform,
  clientId,
  scheduledTimeISO,
  contentType = 'text',
}) {
  const [prediction, setPrediction] = useState(null);
  const [bestTimes, setBestTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBestTimes, setLoadingBestTimes] = useState(false);
  const [error, setError] = useState(null);
  const [competitorId, setCompetitorId] = useState(null);
  const [competitorError, setCompetitorError] = useState(null);
  const predictAbortRef = useRef(null);

  const debouncedContent = useDebouncedValue(content, DEBOUNCE_MS);

  const platformValue =
    (Array.isArray(platform) ? platform[0] : platform)?.toLowerCase?.() ||
    'facebook';

  const scheduledISO =
    typeof scheduledTimeISO === 'string' && scheduledTimeISO
      ? scheduledTimeISO
      : new Date().toISOString();

  const canPredict = Boolean(competitorId && platformValue);
  const canFetchBestTimes = Boolean(clientId && platformValue);

  const fetchCompetitors = useCallback(async () => {
    if (!clientId) {
      setCompetitorId(null);
      setCompetitorError(null);
      return;
    }
    try {
      const data = await competitorsAPI.getCompetitors({ client_id: clientId });
      const list = Array.isArray(data) ? data : data?.competitors || data?.items || [];
      const first = list.find(
        (c) => c.platform === platformValue || !c.platform
      ) || list[0];
      const id = first?.competitor_id ?? first?.id;
      if (id != null) {
        setCompetitorId(id);
        setCompetitorError(null);
      } else {
        setCompetitorId(null);
        setCompetitorError('Add competitors for this client to see predictions.');
      }
    } catch (err) {
      setCompetitorId(null);
      setCompetitorError('Could not load competitors.');
    }
  }, [clientId, platformValue]);

  useEffect(() => {
    fetchCompetitors();
  }, [fetchCompetitors]);

  const fetchPrediction = useCallback(async () => {
    if (!canPredict) return;
    if (predictAbortRef.current) {
      try {
        predictAbortRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    predictAbortRef.current = ac;
    setLoading(true);
    setError(null);
    try {
      const result = await mlAnalysisAPI.predictEngagement({
        content: debouncedContent || '',
        platform: platformValue,
        competitor_id: competitorId,
        scheduled_time: scheduledISO,
        content_type: contentType || 'text',
      });
      if (ac?.signal?.aborted) return;
      setPrediction(result);
    } catch (err) {
      if (ac?.signal?.aborted) return;
      setPrediction(null);
      setError(err.response?.data?.detail || 'Prediction failed. Try again.');
    } finally {
      if (!ac?.signal?.aborted) setLoading(false);
    }
  }, [
    canPredict,
    debouncedContent,
    platformValue,
    competitorId,
    scheduledISO,
    contentType,
  ]);

  useEffect(() => {
    if (!canPredict) {
      setPrediction(null);
      setError(null);
      return undefined;
    }
    fetchPrediction();
    return undefined;
  }, [canPredict, fetchPrediction]);

  // Phase 16.R7: best times depend on client + platform only — not live caption.
  const fetchBestTimes = useCallback(async () => {
    if (!canFetchBestTimes) return;
    setLoadingBestTimes(true);
    try {
      const result = await schedulingAPI.getAISchedulingRecommendations(
        parseInt(clientId, 10),
        'General content',
        platformValue,
        'America/New_York'
      );
      const times =
        result?.best_posting_times || result?.best_posting_times_list || [];
      setBestTimes(Array.isArray(times) ? times : []);
    } catch {
      setBestTimes([]);
    } finally {
      setLoadingBestTimes(false);
    }
  }, [canFetchBestTimes, clientId, platformValue]);

  useEffect(() => {
    if (!canFetchBestTimes) {
      setBestTimes([]);
      return undefined;
    }
    const t = setTimeout(fetchBestTimes, BEST_TIME_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [canFetchBestTimes, fetchBestTimes]);

  const handleRetry = () => {
    setError(null);
    if (canPredict) fetchPrediction();
  };

  if (!clientId) {
    return (
      <Card
        sx={{
          bgcolor: 'rgba(22, 27, 34, 0.5)',
          border: '1px solid rgba(48, 54, 61, 0.4)',
          borderRadius: 2,
          flex: 1,
          minHeight: 280,
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary">
            Select a client to see engagement predictions.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (competitorError) {
    return (
      <Card
        sx={{
          bgcolor: 'rgba(22, 27, 34, 0.5)',
          border: '1px solid rgba(48, 54, 61, 0.4)',
          borderRadius: 2,
          flex: 1,
          minHeight: 280,
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary">
            {competitorError}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        bgcolor: 'rgba(22, 27, 34, 0.5)',
        border: '1px solid rgba(48, 54, 61, 0.4)',
        borderRadius: 2,
        flex: 1,
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#f0f6fc',
            }}
          >
            <TrendingUp sx={{ color: '#28a745', fontSize: 20 }} />
            Predicted Performance
          </Typography>
          <MlEstimateBadge basis="model_prediction" />
        </Box>

        {loading && !prediction ? (
          <Box sx={{ py: 2 }}>
            <Skeleton variant="text" width="80%" height={28} />
            <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="70%" height={24} sx={{ mt: 0.5 }} />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            icon={<ErrorOutline />}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
            sx={{ mt: 1 }}
          >
            {error}
          </Alert>
        ) : prediction ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {loading && (
                <CircularProgress size={16} sx={{ color: '#58a6ff' }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Predicted engagement
            </Typography>
            <Typography variant="h6" sx={{ color: '#28a745', fontWeight: 700 }}>
              ~{Math.round(prediction.predicted_absolute_engagement || 0)} likes
              & comments
            </Typography>
            {prediction.predicted_engagement_rate != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Engagement rate:{' '}
                <strong style={{ color: '#f0f6fc' }}>
                  {Number(prediction.predicted_engagement_rate).toFixed(2)}%
                </strong>
              </Typography>
            )}
            {prediction.confidence_band && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Range: {Math.round(prediction.confidence_band.low)} –{' '}
                {Math.round(prediction.confidence_band.high)}
              </Typography>
            )}
          </Box>
        ) : null}

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(48, 54, 61, 0.3)' }}>
          <MlMetricLabel
            basis={
              bestTimes.length > 0
                ? 'best_time_scheduling'
                : 'best_time_heuristic'
            }
            sx={{ mb: 1 }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#f0f6fc',
              }}
            >
              <Schedule sx={{ fontSize: 18, color: '#ffd700' }} />
              Best time to post
            </Typography>
          </MlMetricLabel>
          {loadingBestTimes && bestTimes.length === 0 ? (
            <Skeleton variant="text" width="90%" />
          ) : bestTimes.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {bestTimes.slice(0, 5).map((time, i) => (
                <Chip
                  key={i}
                  label={time}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: '#8b949e',
                    borderColor: 'rgba(48, 54, 61, 0.6)',
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {canFetchBestTimes
                ? 'More published posts and competitor data are needed to suggest posting times for this client.'
                : 'Select a client to see posting-time suggestions.'}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default memo(EngagementPredictionPanel);
